import { useEffect, useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { completeRecovery, updatePassword, MIN_PASSWORD_LENGTH } from '@/lib/auth';

/**
 * パスワードリセットのメールリンク着地点 (`/auth/reset-password?code=...`)。
 *
 * `auth.callback.tsx` の OAuth コード交換と同じ形（`completeRecovery` が
 * `exchangeCodeForSession` を呼ぶ）。交換成功でセッションが張られたら新しい
 * パスワードのフォームを出す。
 */
function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (typeof window === 'undefined') return;
        await completeRecovery(new URL(window.location.href));
        if (!cancelled) setReady(true);
      } catch (err) {
        if (!cancelled) {
          console.error('[auth] completeRecovery failed', err);
          setLinkError('リンクが無効か期限切れです。もう一度パスワードリセットをお試しください。');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`パスワードは ${MIN_PASSWORD_LENGTH} 文字以上にしてください。`);
      return;
    }
    if (password !== confirmPassword) {
      setFormError('パスワードが一致しません。');
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      await navigate({ to: '/', replace: true });
    } catch (err) {
      console.error('[auth] updatePassword failed', err);
      setFormError('パスワードを更新できませんでした。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>新しいパスワードを設定</CardTitle>
          <CardDescription>アカウントの新しいパスワードを入力してください。</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {linkError ? (
            <>
              <p className="text-sm text-destructive">{linkError}</p>
              <p className="text-center text-sm text-muted-foreground">
                <Link
                  to="/auth/forgot-password"
                  className="text-foreground underline underline-offset-2 hover:opacity-80"
                >
                  もう一度リセットリンクを送る
                </Link>
              </p>
            </>
          ) : !ready ? (
            <p className="text-sm text-muted-foreground">確認中…</p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password">新しいパスワード</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">{MIN_PASSWORD_LENGTH} 文字以上</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="confirm-password">新しいパスワード（確認）</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
              <Button type="submit" disabled={submitting}>
                {submitting ? '更新中…' : 'パスワードを更新'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/auth/reset-password')({
  component: ResetPasswordPage,
});
